import {
  useState, useEffect, SyntheticEvent, useRef
} from 'react';
import {
  StripeCardNumberElement,
  StripeElementChangeEvent,
  PaymentMethod,
  PaymentIntent,
} from '@stripe/stripe-js';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js';
import { Label, crTheme } from '@comicrelief/component-library';

import { ProviderLogos } from '@/components/providerLogos/ProviderLogos';
import { logger } from '@/services/logger/logger';
import paymentFieldPlaceholders from './StripeCardData';
import { useAppSelector, useAppDispatch } from '@/store/hooks';

import { useJourneyNavigate } from '@/services/journeys/useJourneyNavigate';
import { useJourneyOptions } from '@/services/journeys/useJourneyOptions';

import {
  setComplete, clearErrorMessage, setErrorMessage, setShowRecaptchaV2
} from '@/features/paymentProcess/transactionSlice';
import { StripeExecuteResponse } from '@/api/payments/stripeExecute';
import PaymentErrorText from '@/features/paymentProcess/PaymentErrorText';
import { usePaymentSuccess } from '@/features/paymentProcess/usePaymentSuccess';
import { usePaymentFailure } from '@/features/paymentProcess/usePaymentFailure';

import SoftHider from '@/components/softHider/SoftHider';
import Loader from '@/components/loader/Loader';

import RecaptchaLibV3 from '@/libs/recaptcha-lib/components/RecaptchaLibV3';
import RecaptchaLibV2 from '@/libs/recaptcha-lib/components/RecaptchaLibV2';
import { RecaptchaLibToken } from '@/libs/recaptcha-lib/types/types';

import { StyledButtonWithStates } from '../../components/global/Global.style';

import { useStripePayment } from './useStripePayment';
import { useStripeCreate } from './useStripeCreate';
import { useStripeExecute } from './useStripeExecute';
import { useStripeExecuteIntent } from './useStripeExecuteIntent';

import StepBack from '@/components/stepBack/StepBack';
import { ErrorCustomiser } from '@/services/error/ErrorContainer';

import './stripe.css';

import {
  ExpiryCvcWrapper,
  InnerExpiryCvcWrapper,
  ButtonWrapper,
  RecaptchaWrapper
} from './StripeCard.styles';

import { useCartContext } from '@/services/cart/CartContext';

interface StripeCardProps {
  currencySymbol: string;
}

function StripeCard({ currencySymbol }: StripeCardProps) {
  const dispatch = useAppDispatch();
  const paymentSelectState = useAppSelector(state => state.paymentSelect);
  const { cartData } = useCartContext();

  const journeyNavigate = useJourneyNavigate();
  const paymentSuccess = usePaymentSuccess();
  const paymentFailure = usePaymentFailure();
  const { selectedJourney } = useJourneyOptions();

  const { triggerPayment, run3dsAction } = useStripePayment();
  const { triggerExecuteApi } = useStripeExecute();
  const { triggerExecuteIntentApi } = useStripeExecuteIntent();

  const [ready, setReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<RecaptchaLibToken>();

  const [elementsComplete, setElementsComplete] = useState({
    cardNumber: false,
    cardExpiry: false,
    cardCvc: false,
  });
  const formValid = Object.values(elementsComplete).every((state => state)) && !!recaptchaToken;
  const showReCaptchaV2 = useAppSelector(state => state.transaction.showReCaptchaV2);
  const prevShowV2Ref = useRef(showReCaptchaV2);

  const { triggerCreateApi, createData } = useStripeCreate();

  // Track previous showReCaptchaV2 value and handle version switching
  useEffect(() => {
    async function createApi() {
      // Wait for cart data to be loaded
      if (!cartData) {
        return;
      }

      // If switching from v3 to v2, clear the token first
      if (!prevShowV2Ref.current && showReCaptchaV2) {
        setRecaptchaToken(null);
        prevShowV2Ref.current = true;
        return; // Exit early to prevent API call during version switch
      }

      // Update ref for next time
      prevShowV2Ref.current = showReCaptchaV2;

      // Don't make API call if no token
      if (!recaptchaToken) {
        return;
      }

      await triggerCreateApi({
        recaptchaToken,
        reCaptchaVersion: showReCaptchaV2 ? 2 : 3,
        onSuccess: () => {},
        onError: (error?: unknown) => {
          const typedError = error as { status?: number };
          if (typedError?.status === 401 && !showReCaptchaV2) {
            logger.error('Failed reCaptcha v3 check');
            setRecaptchaToken(null);
            dispatch(setShowRecaptchaV2(true));
            return;
          }

          dispatch(setErrorMessage(ErrorCustomiser(error, 'Error initiating Stripe payment')));
          logger.error('Error initiating Stripe payment');
          journeyNavigate('error');
        },
      });
    }

    void createApi();
  }, [dispatch, journeyNavigate, triggerCreateApi, cartData, recaptchaToken, showReCaptchaV2]);

  const onReady = (element: StripeCardNumberElement) => {
    setReady(true);
    element.focus();
  };

  const onElementChange = (event: StripeElementChangeEvent) => {
    const { elementType, complete } = event;

    setElementsComplete({
      ...elementsComplete,
      [elementType]: complete,
    });
  };

  const handleRecaptchaToken = (token: RecaptchaLibToken) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (!recaptchaToken) {
      dispatch(setErrorMessage('Please wait for security verification'));
      return;
    }

    setProcessing(true);

    // Prepare payment-service execute intent following Stripe "requires_action" success for 3DS/SCA scenarios
    // Triggered from stripeExecuteIntent() onsuccess() callback (see below)
    const callExecuteIntentApi = (paymentIntent: PaymentIntent) => {
      void triggerExecuteIntentApi(
        paymentIntent,
        {
          onSuccess: () => {
            setProcessing(false);
            dispatch(setComplete(true));
            dispatch(clearErrorMessage());
            paymentSuccess();
          },
          onError: (error) => {
            setProcessing(false);
            dispatch(setErrorMessage(ErrorCustomiser(error, 'Error processing 3D Secure')));
            paymentFailure();
          },
        },
      );
    };

    // Handle "requires_action" Stripe 3DS/SCA scenarios (if required)
    // Triggered from stripeExecute() onRequiresAction() callback (see below)
    const handle3dsAction = async (data: StripeExecuteResponse) => (
      run3dsAction(data, {
        onSuccess: callExecuteIntentApi,
        onError: (error) => {
          setProcessing(false);
          dispatch(setErrorMessage(ErrorCustomiser(error, 'Error processing 3D Secure')));
          paymentFailure();
        },
      })
    );

    // Prepare payment-service execute following Stripe payment success
    // Triggered from triggerPayment() onSuccess() callback (called below)
    const callExecuteApi = async (paymentMethod: PaymentMethod) => {
      void triggerExecuteApi(
        paymentMethod,
        {
          onSuccess: () => {
            setProcessing(false);
            dispatch(setComplete(true));
            dispatch(clearErrorMessage());
            paymentSuccess();
          },
          onRequiresAction: (data: StripeExecuteResponse) => {
            void handle3dsAction(data);
          },
          onError: (error) => {
            setProcessing(false);
            dispatch(setErrorMessage(ErrorCustomiser(error, 'Error processing payment')));
            paymentFailure();
          },
        },
      );
    };

    // Trigger Stripe payment on form submit
    void triggerPayment({
      onSuccess: (paymentMethod: PaymentMethod) => {
        void callExecuteApi(paymentMethod);
      },
      onError: (error) => {
        setProcessing(false);
        dispatch(setErrorMessage(ErrorCustomiser(error, 'Error processing payment')));
        paymentFailure();
      },
    });
  };

  const elementStyles = {
    // NB: changes to these required an application cache clear
    base: {
      fontSize: '1.1rem',
      color: crTheme.color('grey_4'),
      fontFamily: 'Montserrat, Impact, sans-serif',
      '::placeholder': {
        color: crTheme.color('grey'),
      },
      border: '1px solid #969598',
    },
    complete: {
      color: crTheme.color('green') // Green for successful input
    },
    invalid: {
      color: crTheme.color('red'),
    },
  };

  const shouldShowForm = !!(ready && !!createData && !processing);
  const isPayin = selectedJourney === 'payin';

  return (
    <>
      {/* Show reCAPTCHA v2 first if enabled */}
      {(showReCaptchaV2 && !recaptchaToken) ? (
        <RecaptchaWrapper>
          <RecaptchaLibV2 callback={handleRecaptchaToken} />
        </RecaptchaWrapper>
      ) : (
        <SoftHider show={shouldShowForm} loader={<Loader />}>

          <form onSubmit={event => void handleSubmit(event)}>

            <PaymentErrorText />

            <Label label="Card Number" />
            <CardNumberElement
              options={{
                disableLink: true,
                style: elementStyles,
                placeholder: paymentFieldPlaceholders.cardNumber
              }}
              onReady={onReady}
              onChange={onElementChange}
            />

            <ExpiryCvcWrapper>
              <InnerExpiryCvcWrapper>
                <Label label="Expiration Date" />
                <CardExpiryElement
                  onChange={onElementChange}
                  options={{
                    style: elementStyles,
                    placeholder: paymentFieldPlaceholders.expiryDate
                  }}
                />
              </InnerExpiryCvcWrapper>

              <InnerExpiryCvcWrapper>
                <Label label="CVC" />
                <CardCvcElement
                  onChange={onElementChange}
                  options={{
                    style: elementStyles,
                    placeholder: paymentFieldPlaceholders.CVC
                  }}
                />
              </InnerExpiryCvcWrapper>
            </ExpiryCvcWrapper>

            <ProviderLogos />

            <ButtonWrapper>
              <StyledButtonWithStates
                type="submit"
                disabled={!formValid || processing}
                fullWidth
                data-test="cardPaymentContinue"
              >
                {isPayin ? 'Pay in' : 'Donate'}
                {' '}
                {currencySymbol}
                {paymentSelectState.amount}
                {' '}
                {isPayin ? 'now' : 'once'}
              </StyledButtonWithStates>
            </ButtonWrapper>

            {!showReCaptchaV2 && <RecaptchaLibV3 action="stripeCard" callback={handleRecaptchaToken} />}

          </form>

          <StepBack data-test="cardPaymentGoBack" />
        </SoftHider>
      )}
    </>
  );
}

export default StripeCard;
